import { lazy, Suspense, type ReactNode } from 'react';
import { Label } from '../label';
import { cn } from '../../lib/utils';
import { useFormOptionalLabel } from './form-optional-label';
import { useFormReadOnly } from './form-read-only';

const FieldInfoTooltip = lazy(() => import('./field-info-tooltip'));

// `<label htmlFor>` only auto-focuses native form controls. For contenteditable
// editors (e.g. tiptap's ProseMirror element), the click does nothing — so we
// manually focus the target when the label is clicked.
function focusContentEditableOnClick(e: React.MouseEvent<HTMLLabelElement>, htmlFor: string) {
	const el = document.getElementById(htmlFor);
	if (el?.isContentEditable) {
		e.preventDefault();
		el.focus();
	}
}

export function FieldLabelRow({
	label,
	htmlFor,
	hasError,
	optional,
	info,
	className,
}: {
	label: string;
	htmlFor: string;
	hasError: boolean;
	optional?: boolean;
	info?: ReactNode;
	className?: string;
}) {
	const optionalLabel = useFormOptionalLabel();
	const formReadOnly = useFormReadOnly();

	return (
		<div className={cn('flex items-center justify-between gap-6', className)}>
			<div className="flex h-5 items-center gap-1">
				<Label
					data-slot="form-label"
					data-error={hasError}
					className="data-[error=true]:text-destructive"
					htmlFor={htmlFor}
					onClick={(e) => focusContentEditableOnClick(e, htmlFor)}
				>
					{label}
				</Label>
				{info && (
					<Suspense>
						<FieldInfoTooltip info={info} />
					</Suspense>
				)}
			</div>
			{optional && (
				<span className={cn('text-xs text-muted-foreground transition-opacity', formReadOnly && 'opacity-0')}>
					{optionalLabel}
				</span>
			)}
		</div>
	);
}
